import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import jsQR from 'jsqr';
import { useServer } from '../contexts/ServerContext';
import Cookies from 'js-cookie';

// 创建全局 socket 实例
let globalSocket = null;

// 创建 socket 实例的函数
const createSocket = (serverUrl) => {
  // 如果存在旧的连接，先断开
  if (globalSocket) {
    globalSocket.disconnect();
    globalSocket = null;
  }
  
  globalSocket = io(serverUrl, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    withCredentials: true,  // 启用跨域 cookie
    extraHeaders: {
      'Access-Control-Allow-Credentials': 'true'
    }
  });
  
  return globalSocket;
};

function Publisher() {
  const { serverUrl, isDebugMode } = useServer();
  const [socket, setSocket] = useState(null);
  const [qrCode, setQrCode] = useState('');
  const [subscriptionCode, setSubscriptionCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('请点击 "开始扫描" 授权摄像头。');
  const [debugInfo, setDebugInfo] = useState('');
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);  // 添加调试面板状态
  const [sessionId, setSessionId] = useState('');
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanFrameRef = useRef(null);
  const streamRef = useRef(null);
  const isScanningRef = useRef(false);
  const lastQrCodeRef = useRef('');  // 添加 ref 来记录上一次的二维码内容
  const navigate = useNavigate();

  // 获取可用的摄像头列表
  useEffect(() => {
    const getCameras = async () => {
      try {
        // 首先请求摄像头权限
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: {
            facingMode: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) 
              ? { ideal: 'environment' }  // 移动设备优先使用后置摄像头
              : 'user'  // 桌面设备使用前置摄像头
          } 
        });
        
        // 获取到权限后，停止这个临时流
        stream.getTracks().forEach(track => track.stop());
        
        // 然后获取设备列表
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setCameras(videoDevices);
        
        // 检测是否为移动设备
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        if (isMobile) {
          // 在移动设备上，尝试找到后置摄像头
          const backCamera = videoDevices.find(device => 
            device.label.toLowerCase().includes('back') || 
            device.label.toLowerCase().includes('后置')
          );
          setSelectedCamera(backCamera ? backCamera.deviceId : videoDevices[0].deviceId);
        } else if (videoDevices.length > 0) {
          // 在桌面设备上，默认选择第一个摄像头
          setSelectedCamera(videoDevices[0].deviceId);
        }
        
        setDebugInfo(prev => prev + '\n[成功] 获取到摄像头列表: ' + videoDevices.length + ' 个设备');
      } catch (err) {
        console.error('获取摄像头列表失败:', err);
        setDebugInfo(prev => prev + '\n[错误] 获取摄像头列表失败: ' + err.message);
        setError('无法访问摄像头，请确保：\n1. 已授予摄像头权限\n2. 使用 HTTPS 或 localhost\n3. 设备有可用的摄像头');
        
        // 如果获取设备列表失败，尝试直接使用默认配置
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        setSelectedCamera('default');
        setDebugInfo(prev => prev + '\n[降级] 使用默认摄像头配置');
      }
    };
    getCameras();
  }, []);

  // 组件挂载时初始化 socket 连接
  useEffect(() => {
    const newSocket = createSocket(serverUrl);
    setSocket(newSocket);

    return () => {
      if (globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
      }
    };
  }, [serverUrl]);

  // Socket.IO 事件监听
  useEffect(() => {
    if (!socket) return;

    // 连接事件
    const handleConnect = () => {
      setDebugInfo(prev => prev + '\n[连接] Socket.IO 已连接');
      // 每次连接都请求发布者码，服务器会根据 session 决定是否返回新的
      socket.emit('getPublisherCode');
      setDebugInfo(prev => prev + '\n[请求] 正在获取发布者码...');
    };

    // 断开连接事件
    const handleDisconnect = () => {
      setDebugInfo(prev => prev + '\n[连接] Socket.IO 已断开');
    };

    // 重连事件
    const handleReconnect = (attemptNumber) => {
      setDebugInfo(prev => prev + `\n[连接] Socket.IO 重连成功 (尝试次数: ${attemptNumber})`);
      // 重连后重新请求发布者码
      socket.emit('getPublisherCode');
      setDebugInfo(prev => prev + '\n[请求] 重连后重新获取发布者码...');
    };

    // 重连失败事件
    const handleReconnectFailed = () => {
      setDebugInfo(prev => prev + '\n[连接] Socket.IO 重连失败');
      setError('连接服务器失败，请检查网络连接');
    };

    // 发布码事件
    const handlePublisherCode = (code) => {
      setSubscriptionCode(code);
      const expiryDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      setDebugInfo(prev => prev + `\n[服务器] 获取到发布码: ${code} (将在 ${expiryDate.toLocaleString()} 过期)`);
      setError(''); // 清除可能的错误信息
      setFeedback('请点击 "开始扫描" 授权摄像头。'); // 重置反馈信息
    };

    // 设置 session 事件
    const handleSetSession = (sessionId) => {
      console.log('设置会话:', sessionId);
      // 保存会话ID到 cookie，设置7天过期
      Cookies.set('sessionId', sessionId, { 
        expires: 7,
        path: '/',
        secure: true,
        sameSite: 'none',
        domain: '.9estu.com'  // 允许所有子域名访问
      });
      setSessionId(sessionId);
      console.log('会话已保存到 cookie，过期时间:', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
    };

    // 二维码确认事件
    const handleQrCodeReceived = (data) => {
      setDebugInfo(prev => prev + `\n[服务器] 确认收到二维码: ${data.qrCode}`);
    };

    // 错误事件
    const handleError = (error) => {
      setError(error);  
      setDebugInfo(prev => prev + `\n[错误] ${error}`);
      if (error === '发布者码已过期') {
        // 如果发布者码过期，清除 session
        Cookies.remove('sessionId');
        setSubscriptionCode('');
        setDebugInfo(prev => prev + '\n[系统] 发布者码已过期，已清除 session');
      }
    };

    // 注册事件监听器
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('reconnect', handleReconnect);
    socket.on('reconnect_failed', handleReconnectFailed);
    socket.on('publisherCode', handlePublisherCode);
    socket.on('setSession', handleSetSession);
    socket.on('qrCodeReceived', handleQrCodeReceived);
    socket.on('error', handleError);

    // 组件卸载时清理事件监听器
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('reconnect', handleReconnect);
      socket.off('reconnect_failed', handleReconnectFailed);
      socket.off('publisherCode', handlePublisherCode);
      socket.off('setSession', handleSetSession);
      socket.off('qrCodeReceived', handleQrCodeReceived);
      socket.off('error', handleError);
    };
  }, [socket]);

  // 扫描二维码
  const scanQRCode = () => {
    // 详细检查每个条件
    if (!isScanningRef.current) {
      setDebugInfo(prev => prev + '\n[警告] isScanning 为 false');
      return;
    }
    if (!videoRef.current) {
      setDebugInfo(prev => prev + '\n[警告] videoRef.current 不存在');
      return;
    }
    if (!canvasRef.current) {
      setDebugInfo(prev => prev + '\n[警告] canvasRef.current 不存在');
      return;
    }
    if (!streamRef.current) {
      setDebugInfo(prev => prev + '\n[警告] streamRef.current 不存在');
      return;
    }
    if (!subscriptionCode) {
      setDebugInfo(prev => prev + '\n[警告] 未获取到发布者码');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d', { willReadFrequently: true });

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      try {
        const startTime = performance.now();
        
        // 设置 canvas 尺寸
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // 绘制当前帧
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // 获取图像数据
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        
        // 扫描二维码
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert",
          minSize: 100,
          maxSize: 500,
        });

        const endTime = performance.now();
        const scanTime = endTime - startTime;

        if (code) {
          // 只在二维码内容变化时更新
          if (code.data !== lastQrCodeRef.current) {
            lastQrCodeRef.current = code.data;
            setQrCode(code.data);
            
            // 向服务器发送新的二维码数据
            socket.emit('newQrCode', {
              qrCode: code.data,
              publisherCode: subscriptionCode,
              deviceInfo: {
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                language: navigator.language
              }
            });
            
            setDebugInfo(prev => prev + `\n[${new Date().toLocaleTimeString()}] 扫描到新的二维码: ${code.data} (耗时: ${scanTime.toFixed(2)}ms)`);
          }

          // 计算扫描框位置
          const scanBox = document.getElementById('scan-box');
          if (scanBox) {
            const videoRect = video.getBoundingClientRect();
            const scaleX = videoRect.width / video.videoWidth;
            const scaleY = videoRect.height / video.videoHeight;

            // 根据设备类型计算二维码位置
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            let boxX, boxY;
            
            if (isMobile) {
              // 移动设备（后置摄像头）不需要水平翻转
              boxX = code.location.topLeftCorner.x * scaleX;
              boxY = code.location.topLeftCorner.y * scaleY;
            } else {
              // 桌面设备（前置摄像头）需要水平翻转
              boxX = (video.videoWidth - code.location.bottomRightCorner.x) * scaleX;
              boxY = code.location.topLeftCorner.y * scaleY;
            }

            const boxWidth = (code.location.bottomRightCorner.x - code.location.topLeftCorner.x) * scaleX;
            const boxHeight = (code.location.bottomRightCorner.y - code.location.topLeftCorner.y) * scaleY;

            // 更新扫描框位置和大小
            scanBox.style.left = `${boxX}px`;
            scanBox.style.top = `${boxY}px`;
            scanBox.style.width = `${boxWidth}px`;
            scanBox.style.height = `${boxHeight}px`;
            scanBox.style.display = 'block';
          }
        } else {
          // 如果没有扫描到二维码，隐藏扫描框
          const scanBox = document.getElementById('scan-box');
          if (scanBox) {
            scanBox.style.display = 'none';
          }
          
          // 每100帧输出一次性能信息
          if (Math.random() < 0.01) {
            setDebugInfo(prev => prev + `\n[性能] 扫描耗时: ${scanTime.toFixed(2)}ms, 视频尺寸: ${video.videoWidth}x${video.videoHeight}`);
          }
        }
      } catch (error) {
        setDebugInfo(prev => prev + `\n[错误] 扫描过程出错: ${error.message}`);
      }
    } else {
      setDebugInfo(prev => prev + `\n[警告] 视频数据不足 (readyState: ${video.readyState})`);
    }

    // 继续下一帧扫描
    if (isScanningRef.current) {
      scanFrameRef.current = requestAnimationFrame(scanQRCode);
    }
  };

  // 开始扫描
  const startScanning = async () => {
    try {
      setError('');
      setFeedback('正在请求摄像头权限...');
      setDebugInfo(prev => prev + '\n[开始] 初始化扫描...');

      // 确保 refs 已初始化
      if (!videoRef.current) {
        throw new Error('视频元素未找到');
      }
      if (!canvasRef.current) {
        throw new Error('Canvas 元素未找到');
      }

      const constraints = {
        video: {
          ...(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? {
            // 移动设备配置
            facingMode: { exact: 'environment' },  // 强制使用后置摄像头
            width: { ideal: 720 },
            height: { ideal: 1280 },
            frameRate: { ideal: 30 }
          } : {
            // 桌面设备配置
            deviceId: selectedCamera && selectedCamera !== 'default' ? { exact: selectedCamera } : undefined,
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          })
        }
      };

      setDebugInfo(prev => prev + '\n[配置] 摄像头参数: ' + JSON.stringify(constraints));

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setDebugInfo(prev => prev + '\n[成功] 获取到摄像头流');

      const videoElement = videoRef.current;
      videoElement.srcObject = stream;

      // 等待视频元数据加载
      await new Promise((resolve, reject) => {
        videoElement.onloadedmetadata = async () => {
          try {
            await videoElement.play();
            setDebugInfo(prev => prev + '\n[成功] 视频开始播放');
            setFeedback('摄像头已激活，请对准 QR 码。');
            
            // 确保 canvas 已准备好
            if (canvasRef.current) {
              const context = canvasRef.current.getContext('2d', { willReadFrequently: true });
              if (!context) {
                throw new Error('无法获取 canvas 上下文');
              }
              setDebugInfo(prev => prev + '\n[成功] Canvas 上下文已初始化');
            }
            
            // 设置扫描状态
            isScanningRef.current = true;  // 先设置 ref
            setIsScanning(true);  // 再设置 state
            setDebugInfo(prev => prev + '\n[成功] 设置扫描状态为 true');
            
            // 使用 setTimeout 确保状态更新后再开始扫描
            setTimeout(() => {
              if (isScanningRef.current) {  // 使用 ref 检查状态
                setDebugInfo(prev => prev + '\n[成功] 开始扫描循环');
                scanQRCode();
              } else {
                setDebugInfo(prev => prev + '\n[警告] 扫描状态异常，未开始扫描');
              }
            }, 100);
            
            resolve();
          } catch (err) {
            reject(new Error('视频播放失败: ' + err.message));
          }
        };

        videoElement.onerror = (error) => {
          reject(new Error('视频加载失败: ' + error.message));
        };

        // 设置超时
        setTimeout(() => {
          reject(new Error('视频加载超时'));
        }, 5000);
      });

    } catch (err) {
      console.error('启动扫描失败:', err);
      setError(err.message);
      setFeedback('启动扫描失败: ' + err.message);
      setDebugInfo(prev => prev + '\n[错误] 启动扫描失败: ' + err.message);
      stopScanning();
    }
  };

  // 停止扫描
  const stopScanning = () => {
    console.log('stopScanning');
    isScanningRef.current = false;  // 先设置 ref
    setIsScanning(false);  // 再设置 state
    setFeedback('已停止扫描');
    setDebugInfo(prev => prev + '\n[停止] 扫描已停止');
    
    if (scanFrameRef.current) {
      cancelAnimationFrame(scanFrameRef.current);
      scanFrameRef.current = null;
      setDebugInfo(prev => prev + '\n[清理] 取消动画帧');
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setDebugInfo(prev => prev + '\n[清理] 停止视频流');
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      setDebugInfo(prev => prev + '\n[清理] 清除视频源');
    }
  };

  // 切换摄像头
  const handleCameraChange = async (event) => {
    setSelectedCamera(event.target.value);
    if (isScanning) {
      stopScanning();
      await startScanning();
    }
  };

  // 添加刷新发布者码的函数
  const handleRefreshCode = () => {
    if (socket) {
      // 无论是否在扫描状态，都先确认
      if (window.confirm('确定要刷新发布码吗？刷新后页面将自动重新加载。')) {
        setFeedback('正在刷新发布者码...');
        setDebugInfo(prev => prev + '\n[用户] 请求刷新发布者码');
        socket.emit('refreshPublisherCode');
        // 在发送刷新请求后，等待一小段时间再刷新页面
        setTimeout(() => {
          window.location.reload();
        }, 1000); // 等待1秒，确保服务器有足够时间处理请求
      } else {
        setDebugInfo(prev => prev + '\n[用户] 取消刷新发布者码');
      }
    }
  };

  // // 组件卸载时清理资源
  // useEffect(() => {
  //   return () => {
  //     stopScanning();
  //   };
  // }, []);

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="text-4xl font-bold text-center mb-12">发布模式</h1>
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="space-y-6">
          {subscriptionCode && (
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2">您的发布码</h2>
              <div className="bg-gray-100 p-4 rounded-lg flex items-center justify-between">
                <p className="font-mono text-2xl">{subscriptionCode}</p>
                <div className="flex space-x-2">
                  <button
                    onClick={handleRefreshCode}
                    className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors flex items-center"
                    title="刷新发布者码"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className={/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'hidden' : 'ml-1'}>
                      刷新发布者码
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      const subscribeUrl = `https://fbl.9estu.com/subscribe/${subscriptionCode}`;
                      navigator.clipboard.writeText(subscribeUrl).then(() => {
                        setFeedback('订阅链接已复制到剪贴板');
                        setTimeout(() => setFeedback('请点击 "开始扫描" 授权摄像头。'), 2000);
                      }).catch(err => {
                        setError('复制失败，请手动复制链接');
                        setDebugInfo(prev => prev + '\n[错误] 复制失败: ' + err.message);
                      });
                    }}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    <span className={/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'hidden' : 'ml-1'}>
                      复制订阅链接
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 只在非移动设备上显示摄像头选择 */}
          {cameras.length > 0 && !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                选择摄像头
              </label>
              <select
                value={selectedCamera}
                onChange={handleCameraChange}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {cameras.map((camera) => (
                  <option key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `摄像头 ${camera.deviceId.slice(0, 5)}...`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ 
                transform: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) 
                  ? 'none'  // 移动设备不翻转
                  : 'scaleX(-1)'  // 桌面设备保持翻转
              }}
            />
            <canvas ref={canvasRef} className="hidden" />
            {/* 扫描框指示器 - 只在扫描到二维码时显示 */}
            <div 
              id="scan-box"
              className="absolute border-2 border-red-500 pointer-events-none transition-all duration-200"
              style={{ display: 'none' }}
            />
          </div>

          <div className="flex space-x-4">
            {!isScanning ? (
              <button
                onClick={startScanning}
                className="flex-1 bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-colors"
              >
                开始扫描
              </button>
            ) : (
              <button
                onClick={stopScanning}
                className="flex-1 bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 transition-colors"
              >
                停止扫描
              </button>
            )}
          </div>

          {error && (
            <div className="p-4 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <div className="text-center text-gray-600">
            {feedback}
          </div>

          {qrCode && (
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2">当前扫描的二维码</h2>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="font-mono break-all">{qrCode}</p>
              </div>
            </div>
          )}

          {/* 调试信息面板 - 只在调试模式开启时显示 */}
          {isDebugMode && (
            <div className="mt-4">
              <button
                onClick={() => setIsDebugPanelOpen(!isDebugPanelOpen)}
                className="w-full flex items-center justify-between p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <h2 className="text-xl font-semibold">调试信息</h2>
                <span className="transform transition-transform duration-200" style={{ transform: isDebugPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {isDebugPanelOpen && (
                <div className="mt-2 p-4 bg-gray-100 rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm max-h-60 overflow-y-auto">
                    {debugInfo}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Publisher; 